import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Copy, RefreshCw, Clock } from 'lucide-react';
import { toast } from 'sonner';

const CronParserTool = () => {
  const [cron, setCron] = useState('0 0 * * *');
  
  const parseCron = (expression) => {
    const parts = expression.trim().split(/\s+/);
    if (parts.length !== 5) {
      return { error: 'Cron expression must have 5 parts: minute hour day month weekday' };
    }

    const [minute, hour, day, month, weekday] = parts;
    
    const descriptions = [];
    
    // Minute
    if (minute === '*') descriptions.push('every minute');
    else if (minute.includes('/')) descriptions.push(`every ${minute.split('/')[1]} minutes`);
    else if (minute.includes(',')) descriptions.push(`at minutes ${minute}`);
    else descriptions.push(`at minute ${minute}`);

    // Hour
    if (hour === '*') descriptions.push('of every hour');
    else if (hour.includes('/')) descriptions.push(`every ${hour.split('/')[1]} hours`);
    else if (hour.includes(',')) descriptions.push(`at hours ${hour}`);
    else descriptions.push(`at ${hour}:00`);

    // Day
    if (day !== '*') {
      if (day.includes('/')) descriptions.push(`every ${day.split('/')[1]} days`);
      else if (day.includes(',')) descriptions.push(`on days ${day}`);
      else descriptions.push(`on day ${day}`);
    }

    // Month
    const months = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    if (month !== '*') {
      if (month.includes(',')) descriptions.push(`in months ${month}`);
      else descriptions.push(`in ${months[parseInt(month)] || month}`);
    }

    // Weekday
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    if (weekday !== '*') {
      if (weekday.includes(',')) descriptions.push(`on weekdays ${weekday}`);
      else descriptions.push(`on ${days[parseInt(weekday)] || weekday}`);
    }

    // Common patterns
    if (expression === '0 0 * * *') return { description: 'Every day at midnight' };
    if (expression === '0 * * * *') return { description: 'Every hour' };
    if (expression === '* * * * *') return { description: 'Every minute' };
    if (expression === '0 0 * * 0') return { description: 'Every Sunday at midnight' };
    if (expression === '0 0 1 * *') return { description: 'First day of every month at midnight' };

    return { description: descriptions.join(', '), parts: { minute, hour, day, month, weekday } };
  };

  const result = parseCron(cron);

  const getNextRuns = () => {
    // Simplified next run calculation
    const now = new Date();
    const runs = [];
    for (let i = 0; i < 5; i++) {
      const next = new Date(now.getTime() + (i + 1) * 24 * 60 * 60 * 1000);
      next.setHours(0, 0, 0, 0);
      runs.push(next.toLocaleString());
    }
    return runs;
  };

  const presets = [
    { label: 'Every minute', value: '* * * * *' },
    { label: 'Every hour', value: '0 * * * *' },
    { label: 'Every day at midnight', value: '0 0 * * *' },
    { label: 'Every Sunday', value: '0 0 * * 0' },
    { label: 'Every Monday at 9am', value: '0 9 * * 1' },
    { label: 'First of month', value: '0 0 1 * *' },
  ];

  const copyCron = () => {
    navigator.clipboard.writeText(cron);
    toast.success('Cron expression copied!');
  };

  return (
    <div className="space-y-6" data-testid="cron-parser-tool">
      <div>
        <label className="text-sm text-muted-foreground mb-2 block">Cron Expression</label>
        <Input
          value={cron}
          onChange={(e) => setCron(e.target.value)}
          placeholder="* * * * *"
          className="font-mono"
        />
        <p className="text-xs text-muted-foreground mt-1">Format: minute hour day month weekday</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {presets.map(preset => (
          <Button
            key={preset.value}
            variant={cron === preset.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCron(preset.value)}
          >
            {preset.label}
          </Button>
        ))}
      </div>

      <div className="p-4 rounded-xl bg-muted/30 border border-border">
        <h3 className="font-semibold mb-2 flex items-center gap-2">
          <Clock className="w-4 h-4 text-primary" />
          Human Readable
        </h3>
        {result.error ? (
          <p className="text-red-400">{result.error}</p>
        ) : (
          <p className="text-lg">{result.description}</p>
        )}
      </div>

      {!result.error && (
        <div className="p-4 rounded-xl bg-muted/30 border border-border">
          <h3 className="font-semibold mb-2">Next Scheduled Runs (approximate)</h3>
          <ul className="text-sm text-muted-foreground space-y-1">
            {getNextRuns().map((run, i) => (
              <li key={i} className="font-mono">{run}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={copyCron}>
          <Copy className="w-4 h-4 mr-2" /> Copy Expression
        </Button>
        <Button onClick={() => setCron('* * * * *')} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Reset
        </Button>
      </div>
    </div>
  );
};

export default CronParserTool;
